<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { apiGet, apiSend } from "../api";
import { useToast } from "../stores/toast";

interface CheckoutRow {
  checkoutId: string; step: string; product?: string; converted: boolean;
  orderId?: string; messageCount: number; createdAt?: string; updatedAt?: string;
}

const STEP: Record<string, string> = {
  kiezen: "Kiezen", maat: "Maat kiezen", bevestig: "Bevestigen", sokken: "Sokken-aanbod",
  adres: "Adres", bezorging: "Bezorging", betalen: "Betalen", afgerond: "Afgerond",
};
const tijd = (s?: string) => (s ? new Date(s).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const router = useRouter();
const toast = useToast();
const items = ref<CheckoutRow[]>([]);
const nextCursor = ref<string | null>(null);
const loading = ref(true);
const loadingMore = ref(false);
const error = ref("");
const busy = ref(false);
const fStatus = ref<"alle" | "afgehaakt" | "besteld">("alle");
const selected = ref<Set<string>>(new Set());

const filtered = computed(() =>
  items.value.filter((c) =>
    fStatus.value === "alle" ? true : fStatus.value === "besteld" ? c.converted : !c.converted,
  ),
);
const afgehaakt = computed(() => items.value.filter((c) => !c.converted).length);
const allChecked = computed(() => filtered.value.length > 0 && filtered.value.every((c) => selected.value.has(c.checkoutId)));

async function load(reset = true) {
  const qs = new URLSearchParams({ limit: "50" });
  if (!reset && nextCursor.value) qs.set("cursor", nextCursor.value);
  try {
    const d = await apiGet<{ items: CheckoutRow[]; nextCursor: string | null }>(`/checkout-log?${qs.toString()}`, { auth: true });
    items.value = reset ? d.items : [...items.value, ...d.items];
    items.value.sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""));
    nextCursor.value = d.nextCursor;
    if (reset) selected.value = new Set();
  } catch (e) { error.value = String(e); } finally { loading.value = false; }
}
async function loadMore() { loadingMore.value = true; try { await load(false); } finally { loadingMore.value = false; } }

function toggle(id: string) {
  const s = new Set(selected.value);
  s.has(id) ? s.delete(id) : s.add(id);
  selected.value = s;
}
function toggleAll() {
  selected.value = allChecked.value ? new Set() : new Set(filtered.value.map((c) => c.checkoutId));
}
async function deleteSelected() {
  const ids = Array.from(selected.value);
  if (!ids.length || !confirm(`${ids.length} checkout(s) definitief verwijderen?`)) return;
  busy.value = true;
  try {
    for (const id of ids) await apiSend("DELETE", `/checkout-log/${id}`);
    toast.show(`${ids.length} checkout(s) verwijderd`);
    await load(true);
  } catch (e) { error.value = `Verwijderen mislukt: ${(e as Error).message}`; } finally { busy.value = false; }
}

onMounted(() => load(true));
</script>

<template>
  <div>
    <h1 class="title">Checkouts <span class="count">· {{ afgehaakt }} afgehaakt van {{ items.length }} geladen</span></h1>
    <p class="info">Live tussenstand van elke checkout — zie waar klanten afhaken. Bewaard 1 jaar.</p>

    <div class="frow">
      <button v-for="s in (['alle','afgehaakt','besteld'] as const)" :key="s" class="chip" :class="{ on: fStatus === s }" @click="fStatus = s">
        {{ s === 'alle' ? 'Alle' : s === 'afgehaakt' ? 'Afgehaakt' : 'Besteld' }}
      </button>
      <span class="rescount">{{ filtered.length }} resultaten</span>
    </div>

    <p v-if="loading" class="muted">Laden…</p>
    <p v-else-if="error" class="err">{{ error }}</p>
    <table v-else class="tbl">
      <thead><tr>
        <th class="cb"><input type="checkbox" :checked="allChecked" @change="toggleAll" /></th>
        <th>Product</th><th>Bereikte stap</th><th>Status</th><th>Berichten</th><th>Laatst actief</th>
      </tr></thead>
      <tbody>
        <tr v-for="c in filtered" :key="c.checkoutId" class="clickrow" @click="router.push(`/checkouts/${c.checkoutId}`)">
          <td class="cb" @click.stop><input type="checkbox" :checked="selected.has(c.checkoutId)" @change="toggle(c.checkoutId)" /></td>
          <td>{{ c.product || '—' }}</td>
          <td><span class="stepbadge">{{ STEP[c.step] || c.step }}</span></td>
          <td><span class="status" :class="{ ok: c.converted }">{{ c.converted ? 'Besteld' : 'Afgehaakt' }}</span></td>
          <td>{{ c.messageCount }}</td>
          <td>{{ tijd(c.updatedAt || c.createdAt) }}</td>
        </tr>
        <tr v-if="!filtered.length"><td colspan="6" class="empty">Geen checkouts gevonden.</td></tr>
      </tbody>
    </table>
    <div v-if="!loading && nextCursor" class="more">
      <button :disabled="loadingMore" @click="loadMore">{{ loadingMore ? "Laden…" : "Meer laden" }}</button>
    </div>

    <Transition name="bulk">
      <div v-if="selected.size" class="bulkbar">
        <span>{{ selected.size }} geselecteerd</span>
        <button class="danger" :disabled="busy" @click="deleteSelected">{{ busy ? "Verwijderen…" : "Verwijderen" }}</button>
        <button class="x" @click="selected = new Set()">×</button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.title { font-family: var(--font-ui); font-weight: 700; font-size: 24px; margin: 0 0 6px; }
.count { color: var(--muted); font-weight: 600; }
.info { color: var(--mid); font-size: 13px; margin: 0 0 16px; }
.frow { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; }
.chip { border: 1px solid var(--border-input); background: var(--surface); border-radius: 999px; padding: 6px 13px; font-size: 13px; font-weight: 600; cursor: pointer; }
.chip.on { background: var(--ink); color: #fff; border-color: var(--ink); }
.rescount { margin-left: auto; color: var(--muted); font-size: 13px; }
.muted { color: var(--muted); } .err { color: var(--red); }
.tbl { width: 100%; border-collapse: collapse; font-size: 14px; }
.tbl th { text-align: left; color: var(--muted); font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; padding: 8px 12px; border-bottom: 1px solid var(--hairline); }
.tbl td { padding: 14px 12px; border-bottom: 1px solid var(--hairline); }
.cb { width: 36px; }
.empty { text-align: center; color: var(--muted); padding: 28px 12px; }
.clickrow { cursor: pointer; } .clickrow:hover { background: var(--surface-off); }
.stepbadge { font-size: 12px; font-weight: 600; padding: 3px 9px; border-radius: 999px; background: var(--warm); }
.status { font-size: 13px; font-weight: 600; color: var(--amber); }
.status.ok { color: var(--green); }
.more { display: flex; justify-content: center; margin-top: 18px; }
.more button { border: 1px solid var(--border-input); background: var(--surface); border-radius: 999px; padding: 8px 18px; font-size: 13px; font-weight: 600; cursor: pointer; }
.more button:disabled { opacity: 0.5; cursor: not-allowed; }
.bulkbar {
  position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); z-index: 90;
  display: flex; align-items: center; gap: 14px; background: var(--ink); color: #fff;
  padding: 12px 18px; border-radius: 999px; box-shadow: 0 12px 30px -8px rgba(20,20,20,.5); font-size: 14px;
}
.bulkbar button { border: 0; background: rgba(255,255,255,.15); color: #fff; border-radius: 999px; padding: 7px 14px; font-weight: 600; cursor: pointer; }
.bulkbar button.danger { background: var(--red); }
.bulkbar button.danger:disabled { opacity: 0.6; cursor: not-allowed; }
.bulkbar button.x { background: none; padding: 0 4px; font-size: 18px; }
.bulk-enter-active, .bulk-leave-active { transition: opacity .2s, transform .2s; }
.bulk-enter-from, .bulk-leave-to { opacity: 0; transform: translate(-50%, 10px); }
</style>
